import { useTranslate, useMany } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Progress } from 'antd';
import type {
  PublicationProgressionListOut,
  PublicationListOut,
  ProfileOut,
} from '@/api/generated/schemas';
import { ConsumptionStatus } from '@/api/generated/schemas';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { TextFilter } from '@/components/common/TextFilter';
import { PublicationSelectFilter } from '@/components/user_catalog/publications/PublicationSelectFilter';
import { SelectFilter } from '@/components/common/SelectFilter';
import { NumberRangeFilter } from '@/components/common/NumberRangeFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import {
  ConsumptionStatusTag,
  ProductFormatTag,
} from '@/components/progress/tags';
import { orDash } from '@/components/common/show/shared';
import { formatDateTime } from '@/utils/format';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const ProgressionList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();

  const { tableProps, sorters, setFilters, filters } =
    useAdminTable<PublicationProgressionListOut>({
      pagination: { pageSize: 20 },
      resource: 'progress.progressions',
      sorters: {
        initial: [{ field: 'updated_at', order: 'desc' }],
      },
    });

  const rows = tableProps.dataSource ?? [];

  // Batched once per page: the row components take the record and skip their
  // own fetch, so a page costs two requests instead of one per row.
  const publicationIds = [
    ...new Set(rows.map((r) => r.publication_id).filter(Boolean)),
  ];

  const { result: publicationsResult } = useMany<PublicationListOut>({
    resource: 'user_catalog.publications',
    ids: publicationIds,
    queryOptions: { enabled: publicationIds.length > 0 },
  });

  const publicationMap = new Map<number, PublicationListOut>(
    (publicationsResult?.data ?? []).map((p) => [p.id, p])
  );

  const profileIds = [
    ...new Set(rows.map((r) => r.profile_id).filter(Boolean)),
  ];

  const { result: profilesResult } = useMany<ProfileOut>({
    resource: 'account.profiles',
    ids: profileIds,
    queryOptions: { enabled: profileIds.length > 0 },
  });

  const profileMap = new Map<number, ProfileOut>(
    (profilesResult?.data ?? []).map((p: ProfileOut) => [p.id, p])
  );

  return (
    <List>
      <Table
        {...rowNav('progress.progressions', tableProps)}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="profile_id"
          title={t('progress.progressions.fields.profile_id')}
          render={(value: number) =>
            orDash(
              value != null && (
                <ProfileIconLink
                  profileId={value}
                  profile={profileMap.get(value)}
                />
              )
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'profile_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="profile_id"
              placeholder={t('progress.progressions.filter.profile_id')}
              width={160}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="publication_id"
          title={t('progress.progressions.fields.publication_id')}
          render={(value: number) => (
            <PublicationInfoPopover
              publication={publicationMap.get(value)}
              publicationId={value}
            />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'publication_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <PublicationSelectFilter
              field="publication_id"
              placeholder={t('progress.progressions.filter.publication_id')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="consumption_status"
          title={t('progress.progressions.fields.consumption_status')}
          render={(value: string) =>
            orDash(value && <ConsumptionStatusTag status={value} />)
          }
          filtered={isFieldFiltered(
            filters as CrudFilter[],
            'consumption_status'
          )}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="consumption_status"
              minWidth={180}
              placeholder={t('progress.progressions.filter.consumption_status')}
              options={Object.values(ConsumptionStatus).map((s) => ({
                label: t(`progress.progressions.status.${s}`),
                value: s,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="active_format"
          title={t('progress.progressions.fields.active_format')}
          render={(value: string | null) =>
            orDash(
              value && <ProductFormatTag value={value} labels="progressions" />
            )
          }
        />

        <Table.Column
          dataIndex="progress"
          title={t('progress.progressions.fields.progress')}
          sorter
          sortOrder={getSortOrder(sorters, 'progress')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'progress')}
          filterDropdown={({ confirm, clearFilters }) => (
            <NumberRangeFilter
              field="progress"
              min={0}
              max={100}
              width={150}
              fromPlaceholder={t('progress.progressions.filter.progress_min')}
              toPlaceholder={t('progress.progressions.filter.progress_max')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: number) => (
            <div style={{ minWidth: 130 }}>
              <Progress percent={value} size="small" />
            </div>
          )}
        />

        <Table.Column
          dataIndex="updated_at"
          title={t('base.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'updated_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="updated_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) => orDash(formatDateTime(value))}
        />
      </Table>
    </List>
  );
};
